import React, {ComponentProps, FC} from 'react';

import {Colors, Skeleton, Text} from '@optimaxdev/design-system';
import {Icon, AttentionSeparated, Approve} from '@optimaxdev/icons';
import classNames from 'classnames';

import {PRESCRIPTION_STATUS} from 'constants/success';

import {getIsAllItemsOfOrderUpdated} from 'libs/success/helpers/helpers';
import {ItemsType} from 'types/successPage';

import s from './successTimeline.scss';

const getCommonTextProps = (isMobile?: boolean): ComponentProps<typeof Text> => ({
    bold: !isMobile,
    bolder: isMobile,
    color: Colors.blueCharcoal,
    lineHeight: isMobile ? '14px' : '18px',
    size: isMobile ? '13px' : '14px',
});

export type PropsType = {
    orderItems: ItemsType;
    originalStatus: string;
    isLoading?: boolean;
};

/**
 * The header for the success page
 */
export const SuccessTimeline: FC<PropsType> = ({isLoading, orderItems, originalStatus}) => {
    const isMobile = __BUILD_TYPE__ === 'mobile';
    const isVerification =
        getIsAllItemsOfOrderUpdated(orderItems) ||
        originalStatus === PRESCRIPTION_STATUS.UPLOAD ||
        originalStatus === PRESCRIPTION_STATUS.FILL_SUCCESS;

    if (isLoading) {
        return (
            <div className={s.container}>
                <Skeleton height="24px" radius="200px" width="100%" />
            </div>
        );
    }

    return (
        <div className={classNames(s.container, {[s.mobile]: isMobile})}>
            <div
                className={classNames(s.stepItems, {
                    [s.isVerification]: isVerification,
                    [s.mobile]: isMobile,
                })}
            >
                <div className={s.stepWrapper}>
                    <div
                        className={classNames(s.stepCount, {
                            [s.isAttention]: !isVerification,
                            [s.mobile]: isMobile,
                        })}
                    />
                    <Text
                        {...getCommonTextProps(isMobile)}
                        className={classNames(s.stepName, s.left, {[s.mobile]: isMobile})}
                    >
                        Order
                    </Text>
                </div>
                <div
                    className={classNames(s.stepLine, {
                        [s.isAttention]: !isVerification,
                        [s.mobile]: isMobile,
                    })}
                />
                <div className={s.stepWrapper}>
                    <div
                        className={classNames(s.stepCount, {
                            [s.big]: !isVerification,
                            [s.isAttention]: !isVerification,
                            [s.mobile]: isMobile,
                        })}
                    >
                        {!isVerification && (
                            <Icon
                                color={Colors.orangeRoughy}
                                height={11}
                                icon={AttentionSeparated}
                                strokeWidth={2}
                                width={11}
                            />
                        )}
                    </div>
                    <Text
                        {...getCommonTextProps(isMobile)}
                        className={classNames(s.stepName, {
                            [s.isAttention]: !isVerification,
                            [s.mobile]: isMobile,
                        })}
                    >
                        Prescription
                    </Text>
                </div>
                <div className={classNames(s.stepLine, {[s.mobile]: isMobile})} />
                <div className={s.stepWrapper}>
                    <div
                        className={classNames(s.stepCount, {
                            [s.isVerification]: isVerification,
                            [s.mobile]: isMobile,
                        })}
                    >
                        {isVerification && (
                            <Icon
                                color={Colors.jade}
                                height={11}
                                icon={Approve}
                                strokeWidth={2}
                                width={11}
                            />
                        )}
                    </div>
                    <Text
                        {...getCommonTextProps(isMobile)}
                        className={classNames(s.stepName, s.right, {
                            [s.isVerification]: isVerification,
                            [s.mobile]: isMobile,
                        })}
                    >
                        Verification
                    </Text>
                </div>
            </div>
        </div>
    );
};
